import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * The hub-authorable signal catalogue (`/v1/signal-types`, OPSFL-FLOWOPS-1 Track B / B1). This
 * is what makes the ratified machine rail (`POST /api/v1/signals`) a real integration door
 * rather than a one-type demo: a hub admin registers a business event type here, external
 * systems HMAC-post it, each landed signal raises a SIGNAL alert, and the type is wired to a
 * flow under the flow's **Triggers** (the `(SIGNAL, type_key)` binding — Track A's surface).
 *
 * Keys: `alerts.rules.read` (list) / `alerts.rules.write` (create, enable, disable) — reused, no
 * new matrix key: a signal type is exactly as much an automation grant as a routing rule (the
 * console test-fire set the precedent). No delete: disable is the retirement — the intake refuses
 * a DISABLED type 422 from the next post; history keeps.
 */

/** Closed status vocabulary (lc-0010 CHECK). PLATFORM rows are ACTIVE or RESERVED; HUB rows ACTIVE or DISABLED. */
export const SIGNAL_TYPE_STATUSES = ['ACTIVE', 'RESERVED', 'DISABLED'] as const;
export type SignalTypeStatus = (typeof SIGNAL_TYPE_STATUSES)[number];
export const SIGNAL_TYPE_ORIGINS = ['PLATFORM', 'HUB'] as const;
export type SignalTypeOrigin = (typeof SIGNAL_TYPE_ORIGINS)[number];

/** The intake's key shape (`SignalTypeKey.PATTERN`): upper-snake, letter first, ≤ 64 chars. */
export const SIGNAL_TYPE_KEY_PATTERN = /^[A-Z][A-Z0-9_]{0,63}$/;
/** Advisory correlation attribute shape (`NormalizedSignal.CorrelationHint.ATTRIBUTE_PATTERN`). */
export const CORRELATION_ATTRIBUTE_PATTERN = /^[a-z][a-z0-9_.]{0,62}$/;
export const MAX_CORRELATION_ATTRIBUTES = 10;

/** One catalogue row (AlertDtos.SignalTypeView). */
export interface SignalTypeView {
  typeKey: string;
  title: string;
  description: string | null;
  status: SignalTypeStatus | (string & {});
  origin: SignalTypeOrigin | (string & {});
  /** ADVISORY ordered entity-attribute keys senders supply as correlation_hints (strongest first). */
  correlationAttributes: string[];
  /** Regulatory citation — PLATFORM rows only; null for hub-authored business events. */
  citation: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface SignalTypeListView {
  types: SignalTypeView[];
  totalCount: number;
}

/** The CREATE body (AlertDtos.SignalTypeRequest). */
export interface SignalTypeRequest {
  typeKey: string;
  title: string;
  description?: string | null;
  correlationAttributes?: string[] | null;
}

function guardType(t: SignalTypeView): SignalTypeView {
  return {
    ...t,
    correlationAttributes: Array.isArray(t?.correlationAttributes) ? t.correlationAttributes : [],
  };
}

/** GET /v1/signal-types — every type of the hub, platform-reserved first then hub-authored (`alerts.rules.read`). */
export async function listSignalTypes(): Promise<SignalTypeListView> {
  const { data } = await apiClient.get<ApiEnvelope<SignalTypeListView>>('/v1/signal-types');
  const body = unwrap(data);
  const types = Array.isArray(body?.types) ? body.types.map(guardType) : [];
  return { types, totalCount: body?.totalCount ?? types.length };
}

/**
 * POST /v1/signal-types — register a hub-authored type, ACTIVE at once (`alerts.rules.write`).
 * Server refusals are named: 400 INVALID_REQUEST (key/title/attribute shape), 409
 * SIGNAL_TYPE_KEY_TAKEN (never deleted — enable the existing one), 503 when the intake module is off.
 */
export async function createSignalType(request: SignalTypeRequest): Promise<SignalTypeView> {
  const { data } = await apiClient.post<ApiEnvelope<SignalTypeView>>(
    '/v1/signal-types',
    request,
    idempotentConfig(),
  );
  return guardType(unwrap(data));
}

/**
 * POST /v1/signal-types/{key}/enable|disable — the ONLY mutations an existing type supports (no
 * edit, no delete). Platform-reserved rows refuse with 422 SIGNAL_TYPE_PLATFORM_RESERVED.
 */
export async function setSignalTypeEnabled(
  typeKey: string,
  enabled: boolean,
): Promise<SignalTypeView> {
  const { data } = await apiClient.post<ApiEnvelope<SignalTypeView>>(
    `/v1/signal-types/${encodeURIComponent(typeKey)}/${enabled ? 'enable' : 'disable'}`,
    null,
    idempotentConfig(),
  );
  return guardType(unwrap(data));
}
